import { ref, computed, watch, onBeforeUnmount, Ref } from 'vue';
import { useElementBounding } from '@vueuse/core';
import eventBus from '@ve/services/eventBus';

interface AnchorObserver {
    observe: (container: HTMLElement) => void;
    disconnect: () => void;
}

interface AnchorObserverContext {
    el: HTMLElement;
    leadBannerAnchor?: AnchorObserver | null;
}

export function createAnchorObserver(channel: string, selector: string, context: AnchorObserverContext): AnchorObserver {
    let observer: IntersectionObserver | null = null;

    context.leadBannerAnchor?.disconnect();

    const instance: AnchorObserver = {
        observe(container: HTMLElement) {
            this.disconnect();
            const target = container.querySelector<HTMLElement>(selector);
            if (!target) return;

            observer = new IntersectionObserver((entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting) {
                    eventBus.$emit(`portal-anchor:${channel}`, entry.target);
                }
            });
            observer.observe(target);
        },
        disconnect() {
            observer?.disconnect();
            observer = null;
        },
    };

    instance.observe(context.el);

    return instance;
}

interface PortalAnchorReturn {
    anchorPosition: Ref<{ top: string; left: string } | null>;
    resetAnchor: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function usePortalAnchor(
    channel: string,
    floatingComponentRef: Ref<any>,
): PortalAnchorReturn {
    const anchorEl = ref<HTMLElement | null>(null);
    const top = ref('');
    const left = ref('');
    const width = ref('');

    const floatingEl = computed(() => floatingComponentRef.value?.$el as HTMLElement | undefined);
    const { height: floatingHeight } = useElementBounding(floatingEl);
    
    const anchorPosition = computed(() => {
        if (top.value || left.value || width.value) {
            return {
                top: top.value,
                left: left.value,
                width: width.value,
            }
        }
        return null;
    })

    const eventName = `portal-anchor:${channel}`;

    const onAnchorVisible = (el: HTMLElement) => {
        top.value = `${el.offsetTop}px`;
        left.value = `${el.offsetLeft}px`;
        width.value = `${el.offsetWidth}px`;
        anchorEl.value = el;
    };

    eventBus.$on(eventName, onAnchorVisible);

    const resetAnchor = () => {
        top.value = '';
        left.value = '';
        width.value = '';
        anchorEl.value = null;
    };

    watch([floatingHeight, anchorEl], ([height, anchor]) => {
        if (height && anchor) {
            anchor.style.height = `${Math.round(height)}px`;
        }
    });

    onBeforeUnmount(() => {
        eventBus.$off(eventName, onAnchorVisible);
    });

    return {
        anchorPosition,
        resetAnchor,
    };
}
